import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { emailSchema } from "@/lib/form-validation"

interface CalculatorState {
  platform: "web" | "web-mobile"
  delivery: "14" | "7" | "3"
  designProvided: boolean
  designDepth: "wireframes" | "hi-fi" | "brand-system"
  brandVoiceGuide: boolean
  auth: boolean
  dashboard: boolean
  teamManagement: boolean
  subscriptionBilling: boolean
  realtimeCollaboration: boolean
  onboardingFlow: boolean
  aiIntegration: "none" | "basic" | "advanced"
  aiAutomation: "none" | "workflow" | "multi-agent"
  integrations: number
  dataMigration: "none" | "spreadsheet" | "legacy"
  compliance: "none" | "gdpr" | "hipaa"
  analyticsStack: "basic" | "advanced"
  qaAutomation: boolean
  releasePipelines: "standard" | "enterprise"
  documentation: "lite" | "detailed"
  successEnablement: "none" | "playbook" | "training"
  launchVideo: boolean
  postLaunchSupport: "none" | "30" | "60"
  launchSupport: boolean
}

interface MvpCalculatorWidgetProps {
  mode?: "page" | "modal"
  onQuoteSaved?: () => void
}

interface TimelinePhase {
  title: string
  days: number
  description: string
}

interface CostAdjustment {
  label: string
  amount: number
}

interface QuoteBreakdown {
  price: number
  timelineDays: number
  phases: TimelinePhase[]
  costBreakdown: CostAdjustment[]
  deliverables: string[]
}

const LIMITED_TIME_DISCOUNT_RATE = 0.3435
const MINIMUM_DISCOUNTED_PRICE = 4999

const defaultState: CalculatorState = {
  platform: "web",
  delivery: "14",
  designProvided: false,
  designDepth: "hi-fi",
  brandVoiceGuide: true,
  auth: true,
  dashboard: true,
  teamManagement: false,
  subscriptionBilling: false,
  realtimeCollaboration: false,
  onboardingFlow: true,
  aiIntegration: "basic",
  aiAutomation: "workflow",
  integrations: 2,
  dataMigration: "spreadsheet",
  compliance: "none",
  analyticsStack: "basic",
  qaAutomation: false,
  releasePipelines: "standard",
  documentation: "lite",
  successEnablement: "playbook",
  launchVideo: false,
  postLaunchSupport: "30",
  launchSupport: true,
}

const TIMELINE_DESCRIPTIONS: Record<string, string> = {
  discovery: "Founder interviews, success metrics, technical scope lock, roadmap",
  experience: "Product flows, hi-fi UI, component system, responsive states",
  engineering: "Supabase schema, integrations, AI workflows, QA environments",
  launch: "QA, instrumentation, handover, GTM enablement, launch checklist",
}

function calculateQuote(state: CalculatorState): QuoteBreakdown {
  let price = 7500
  if (state.delivery === "7") price *= 1.35
  if (state.delivery === "3") price *= 1.9
  if (state.platform === "web-mobile") price += 2200

  const baseDays = Number(state.delivery)
  const phaseDays: Record<"discovery" | "experience" | "engineering" | "launch", number> = {
    discovery: Math.max(1, Math.round(baseDays * 0.2)),
    experience: Math.max(2, Math.round(baseDays * 0.3)),
    engineering: Math.max(2, Math.round(baseDays * 0.35)),
    launch: Math.max(1, Math.round(baseDays * 0.15)),
  }
  const costBreakdown: CostAdjustment[] = []
  const deliverables = new Set<string>([
    "Production-ready Supabase backend",
    "Authentication, session management, and RBAC",
    "Responsive React + Tailwind UI build",
    "Deployment pipeline & observability setup",
  ])
  let extraDays = 0

  const addAdjustment = (label: string, amount: number, options?: { phase?: keyof typeof phaseDays; days?: number; deliverable?: string }) => {
    if (amount !== 0) {
      price += amount
      costBreakdown.push({ label, amount })
    }
    if (!options) return
    if (options.days) {
      extraDays += options.days
      if (options.phase) phaseDays[options.phase] += options.days
    }
    if (options.deliverable) deliverables.add(options.deliverable)
  }

  if (!state.designProvided) {
    if (state.designDepth === "wireframes") addAdjustment("Low-fidelity UX wireframes", 900, { phase: "experience", days: 1, deliverable: "User journey maps & wireframes" })
    if (state.designDepth === "hi-fi") addAdjustment("High-fidelity UI design", 1500, { phase: "experience", days: 1, deliverable: "Figma hi-fi screens + component library" })
    if (state.designDepth === "brand-system") addAdjustment("Brand identity + design system", 2400, { phase: "experience", days: 2, deliverable: "Brand kit, tokens, and component documentation" })
  } else {
    addAdjustment("Design adaptation from provided assets", 300, { phase: "experience", deliverable: "Design QA & component parity audit" })
  }

  if (state.brandVoiceGuide) addAdjustment("Brand voice & onboarding copywriting", 450, { phase: "discovery", deliverable: "Messaging playbook & onboarding scripts" })

  if (!state.auth) addAdjustment("Skip auth & payments", -600)
  if (!state.dashboard) addAdjustment("No internal dashboard", -850)
  if (state.teamManagement) addAdjustment("Multi-role team management", 1400, { phase: "engineering", days: 1, deliverable: "Team roles, invitations, and permissions" })
  if (state.subscriptionBilling) addAdjustment("Subscription billing & proration", 1200, { phase: "engineering", days: 1, deliverable: "Stripe billing flows & webhooks" })
  if (state.realtimeCollaboration) addAdjustment("Real-time collaboration features", 1000, { phase: "engineering", days: 1, deliverable: "Presence indicators & live updates" })
  if (state.onboardingFlow) addAdjustment("Guided onboarding & activation", 550, { phase: "experience", deliverable: "Progressive onboarding & email nudges" })

  if (state.aiIntegration === "basic") addAdjustment("Single AI capability integration", 1300, { phase: "engineering", deliverable: "AI API integration & prompt orchestration" })
  if (state.aiIntegration === "advanced") addAdjustment("Advanced AI stack (vector DB, evals)", 2600, { phase: "engineering", days: 1, deliverable: "Vector search, eval harness, guardrails" })
  if (state.aiAutomation === "workflow") addAdjustment("Multi-step workflow automation", 900, { phase: "engineering", deliverable: "AI workflow orchestrations + fallbacks" })
  if (state.aiAutomation === "multi-agent") addAdjustment("Multi-agent automation layer", 1900, { phase: "engineering", days: 1, deliverable: "Agent graph with memory & escalations" })

  if (state.integrations > 0) addAdjustment(`${state.integrations} third-party integration${state.integrations > 1 ? "s" : ""}`, state.integrations * 450, { phase: "engineering", days: state.integrations >= 3 ? 1 : 0, deliverable: "Integration playbook & webhook monitoring" })

  if (state.dataMigration === "spreadsheet") addAdjustment("Seed data import (CSV/Notion)", 550, { phase: "engineering", deliverable: "Automated CSV import workflow" })
  if (state.dataMigration === "legacy") addAdjustment("Legacy system migration", 1800, { phase: "engineering", days: 1, deliverable: "Data migration scripts & validation report" })

  if (state.compliance === "gdpr") addAdjustment("GDPR compliance & consent flows", 650, { phase: "discovery", deliverable: "Privacy controls & data handling matrix" })
  if (state.compliance === "hipaa") addAdjustment("HIPAA readiness & audit trail", 2100, { phase: "engineering", days: 1, deliverable: "Audit logging & compliance checklist" })

  if (state.analyticsStack === "advanced") addAdjustment("Advanced analytics + product usage", 700, { phase: "launch", deliverable: "Product analytics dashboards & alerts" })

  if (state.qaAutomation) addAdjustment("Automated QA & regression suite", 950, { phase: "engineering", days: 1, deliverable: "Playwright test suite & monitoring" })
  if (state.releasePipelines === "enterprise") addAdjustment("Enterprise-grade release pipelines", 1100, { phase: "engineering", deliverable: "Multi-environment CI/CD with approvals" })

  if (state.documentation === "detailed") addAdjustment("Detailed documentation + runbooks", 700, { phase: "launch", deliverable: "Developer + operator runbooks" })

  if (state.successEnablement === "playbook") addAdjustment("Customer success playbook", 400, { phase: "launch", deliverable: "Success playbook & lifecycle emails" })
  if (state.successEnablement === "training") addAdjustment("Live training + Loom academy", 950, { phase: "launch", days: 1, deliverable: "Recorded training academy & live sessions" })

  if (state.launchVideo) addAdjustment("Launch video package", 600, { phase: "launch", deliverable: "Launch teaser video + script" })
  if (state.postLaunchSupport === "60") addAdjustment("60-day hypercare extension", 650, { phase: "launch", deliverable: "60-day concierge support" })
  if (state.postLaunchSupport === "none") addAdjustment("Opt-out of post-launch support", -350)

  if (state.launchSupport) addAdjustment("GTM launch assets", 500, { phase: "launch", deliverable: "Landing page copy, email drips, and launch checklist" })

  const timelineDays = baseDays + extraDays
  const sumPhaseDays = Object.values(phaseDays).reduce((total, value) => total + value, 0)
  if (sumPhaseDays < timelineDays) phaseDays.engineering += timelineDays - sumPhaseDays
  if (sumPhaseDays > timelineDays) phaseDays.launch = Math.max(1, phaseDays.launch - (sumPhaseDays - timelineDays))

  const phases: TimelinePhase[] = [
    { title: "Discovery & Strategy", days: phaseDays.discovery, description: TIMELINE_DESCRIPTIONS.discovery },
    { title: "Product Experience", days: phaseDays.experience, description: TIMELINE_DESCRIPTIONS.experience },
    { title: "Engineering & Integrations", days: phaseDays.engineering, description: TIMELINE_DESCRIPTIONS.engineering },
    { title: "QA, Launch & Enablement", days: phaseDays.launch, description: TIMELINE_DESCRIPTIONS.launch },
  ]

  const sortedCostBreakdown = costBreakdown.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).slice(0, 6)

  const discountedPrice = Math.max(
    MINIMUM_DISCOUNTED_PRICE,
    Math.round(price * LIMITED_TIME_DISCOUNT_RATE),
  )

  return {
    price: discountedPrice,
    timelineDays,
    phases,
    costBreakdown: sortedCostBreakdown,
    deliverables: Array.from(deliverables),
  }
}

export function MvpCalculatorWidget({ mode = "page", onQuoteSaved }: MvpCalculatorWidgetProps) {
  const [state, setState] = useState<CalculatorState>(defaultState)
  const [email, setEmail] = useState("")
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const quote = useMemo(() => calculateQuote(state), [state])

  const handleSaveQuote = async () => {
    // Validate email using Zod
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      toast.error(emailValidation.error.errors[0].message);
      return;
    }

    setIsSaving(true);
    try {
      // Use rate-limited edge function
      const { data: response, error } = await supabase.functions.invoke('submit-form', {
        body: {
          email: emailValidation.data,
          type: 'quote',
          data: {
            email: emailValidation.data,
            estimated_price: quote.price,
            status: "new",
            quote_details: {
              source: mode === "modal" ? "mvp-calculator-modal" : "mvp-calculator-page",
              ...state,
              timelineDays: quote.timelineDays,
              deliverables: quote.deliverables,
              capturedAt: new Date().toISOString(),
            },
          }
        }
      });

      if (error || !response?.success) {
        toast.error(response?.error || "Failed to save quote. Please try again.");
        setIsSaving(false);
        return;
      }

      toast.success("Quote saved! Check your email for the PDF.");
      setEmail("");
      setIsSaving(false);
      if (mode === "page") setIsDialogOpen(false);
      if (onQuoteSaved) onQuoteSaved();
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      setIsSaving(false);
    }
  }

  const renderEmailCapture = () => {
    if (mode === "modal") {
      return (
        <div className="space-y-4 rounded-xl border border-border bg-background/80 p-4 shadow-inner">
          <div>
            <Label htmlFor="modal-calculator-email">Send this quote to my inbox</Label>
            <Input
              id="modal-calculator-email"
              type="email"
              placeholder="founder@startup.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-2"
            />
          </div>
          <Button onClick={handleSaveQuote} disabled={isSaving} className="w-full">
            {isSaving ? "Sending..." : "Email me this estimate"}
          </Button>
        </div>
      )
    }

    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button className="mb-3 w-full" size="lg">
            Generate & Save My Quote
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Your Quote</DialogTitle>
            <DialogDescription>
              Enter your email to receive a detailed PDF quote with all your selections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="page-calculator-email">Work Email</Label>
              <Input
                id="page-calculator-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <Button onClick={handleSaveQuote} className="w-full" disabled={isSaving}>
              {isSaving ? "Sending..." : "Send My Quote"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className={mode === "page" ? "container mx-auto px-6 lg:px-12" : "flex flex-col gap-6"}>
      <div className={mode === "page" ? "mx-auto max-w-6xl" : "flex flex-col gap-6"}>
        {mode === "page" ? (
          <div className="mb-12 space-y-4 text-center md:mb-16">
            <h1 className="text-4xl font-medium text-foreground md:text-6xl">
              Get Your Instant MVP Estimate
            </h1>
            <p className="mx-auto max-w-3xl text-lg text-muted-foreground md:text-xl">
              Toggle the scope, AI stack, and launch deliverables to see an investor-ready MVP build budget with the exact timeline breakdown.
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-center">
            <h3 className="text-2xl font-heading text-foreground">Instant MVP Cost Forecast</h3>
            <p className="text-sm text-muted-foreground">
              Adjust scope, AI automation, and enablement deliverables. Save it to lock the pricing for seven days.
            </p>
          </div>
        )}

        <div className={mode === "page" ? "flex flex-col gap-8 lg:flex-row" : "flex flex-col gap-6 lg:flex-row"}>
          <div className="flex-1 space-y-6">
            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-2xl">Foundation</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Scope the platform footprint and sprint cadence.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div>
                  <Label className="text-base">Target platform</Label>
                  <RadioGroup value={state.platform} onValueChange={(value) => setState(current => ({ ...current, platform: value as CalculatorState["platform"] }))} className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2">
                      <RadioGroupItem value="web" id={`${mode}-platform-web`} />
                      <Label htmlFor={`${mode}-platform-web`} className="cursor-pointer">Web app</Label>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2">
                      <RadioGroupItem value="web-mobile" id={`${mode}-platform-web-mobile`} />
                      <Label htmlFor={`${mode}-platform-web-mobile`} className="cursor-pointer">Web + mobile (PWA)</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-base">Sprint delivery target</Label>
                  <Select value={state.delivery} onValueChange={(value) => setState(current => ({ ...current, delivery: value as CalculatorState["delivery"] }))} >
                    <SelectTrigger className="mt-3">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="14">14 days · Standard MVP</SelectItem>
                      <SelectItem value="7">7 days · Priority sprint</SelectItem>
                      <SelectItem value="3">3 days · Flashbuild</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-2xl">Experience & Product Surface</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Define how deep we go on design, collaboration, and monetisation.
              </p>
              <div className="mt-6 space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-2">
                      <Label htmlFor={`${mode}-design`} className="text-sm font-medium">We already have polished designs</Label>
                      <Switch
                        id={`${mode}-design`}
                        checked={state.designProvided}
                        onCheckedChange={(checked) => setState(current => ({ ...current, designProvided: checked }))}
                      />
                    </div>
                    <Label className="text-base">Design depth required</Label>
                    <Select
                      value={state.designDepth}
                      onValueChange={(value) => setState(current => ({ ...current, designDepth: value as CalculatorState["designDepth"] }))}
                      disabled={state.designProvided}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select design depth" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wireframes">Wireframes & UX maps</SelectItem>
                        <SelectItem value="hi-fi">Hi-fi UI & responsive states</SelectItem>
                        <SelectItem value="brand-system">Brand system + component library</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-2">
                      <Label htmlFor={`${mode}-brand-voice`} className="text-sm font-medium">Brand voice & onboarding content</Label>
                      <Switch
                        id={`${mode}-brand-voice`}
                        checked={state.brandVoiceGuide}
                        onCheckedChange={(checked) => setState(current => ({ ...current, brandVoiceGuide: checked }))}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-2">
                      <Label htmlFor={`${mode}-onboarding`} className="text-sm font-medium">Guided onboarding flow</Label>
                      <Switch
                        id={`${mode}-onboarding`}
                        checked={state.onboardingFlow}
                        onCheckedChange={(checked) => setState(current => ({ ...current, onboardingFlow: checked }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <FeatureToggle
                    id={`${mode}-auth`}
                    label="Authentication & payments"
                    checked={state.auth}
                    onCheckedChange={(checked) => setState(current => ({ ...current, auth: checked }))}
                  />
                  <FeatureToggle
                    id={`${mode}-dashboard`}
                    label="Operator dashboard / admin"
                    checked={state.dashboard}
                    onCheckedChange={(checked) => setState(current => ({ ...current, dashboard: checked }))}
                  />
                  <FeatureToggle
                    id={`${mode}-team`}
                    label="Team management & roles"
                    checked={state.teamManagement}
                    onCheckedChange={(checked) => setState(current => ({ ...current, teamManagement: checked }))}
                  />
                  <FeatureToggle
                    id={`${mode}-realtime`}
                    label="Real-time collaboration"
                    checked={state.realtimeCollaboration}
                    onCheckedChange={(checked) => setState(current => ({ ...current, realtimeCollaboration: checked }))}
                  />
                  <FeatureToggle
                    id={`${mode}-subscription`}
                    label="Subscription billing & upgrades"
                    checked={state.subscriptionBilling}
                    onCheckedChange={(checked) => setState(current => ({ ...current, subscriptionBilling: checked }))}
                  />
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-2xl">AI, Data & Integrations</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose the intelligence layer, workflow automation, and data lift required.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div className="space-y-3">
                  <Label className="text-base">AI capability scope</Label>
                  <Select value={state.aiIntegration} onValueChange={(value) => setState(current => ({ ...current, aiIntegration: value as CalculatorState["aiIntegration"] }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="AI stack" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No AI features</SelectItem>
                      <SelectItem value="basic">Single model integration</SelectItem>
                      <SelectItem value="advanced">Advanced (vector DB, evals, guardrails)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Automation sophistication</Label>
                  <Select value={state.aiAutomation} onValueChange={(value) => setState(current => ({ ...current, aiAutomation: value as CalculatorState["aiAutomation"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No automation</SelectItem>
                      <SelectItem value="workflow">Workflow automation & triggers</SelectItem>
                      <SelectItem value="multi-agent">Multi-agent orchestration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">3rd-party integrations</Label>
                  <Input
                    id={`${mode}-integrations`}
                    type="number"
                    min="0"
                    max="10"
                    value={state.integrations}
                    onChange={(event) => {
                      const parsed = parseInt(event.target.value, 10)
                      const next = Number.isNaN(parsed) ? 0 : Math.min(10, Math.max(0, parsed))
                      setState(current => ({ ...current, integrations: next }))
                    }}
                  />
                  <p className="text-xs text-muted-foreground">Include CRMs, payment processors, communications, data sources.</p>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Data migration plan</Label>
                  <Select value={state.dataMigration} onValueChange={(value) => setState(current => ({ ...current, dataMigration: value as CalculatorState["dataMigration"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None required</SelectItem>
                      <SelectItem value="spreadsheet">Spreadsheet / Notion import</SelectItem>
                      <SelectItem value="legacy">Legacy system migration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Compliance envelope</Label>
                  <Select value={state.compliance} onValueChange={(value) => setState(current => ({ ...current, compliance: value as CalculatorState["compliance"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">General SaaS</SelectItem>
                      <SelectItem value="gdpr">GDPR (EU data residency)</SelectItem>
                      <SelectItem value="hipaa">HIPAA (PHI handling)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Analytics instrumentation</Label>
                  <Select value={state.analyticsStack} onValueChange={(value) => setState(current => ({ ...current, analyticsStack: value as CalculatorState["analyticsStack"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Baseline product analytics</SelectItem>
                      <SelectItem value="advanced">Advanced dashboards & alerts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-2xl">Ops, QA & Enablement</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Lock in the engineering quality gates, documentation, and GTM support you need.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <FeatureToggle
                  id={`${mode}-qa`}
                  label="Automated QA & regression suite"
                  checked={state.qaAutomation}
                  onCheckedChange={(checked) => setState(current => ({ ...current, qaAutomation: checked }))}
                />
                <div className="space-y-3">
                  <Label className="text-base">Release pipelines</Label>
                  <Select value={state.releasePipelines} onValueChange={(value) => setState(current => ({ ...current, releasePipelines: value as CalculatorState["releasePipelines"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard CI/CD</SelectItem>
                      <SelectItem value="enterprise">Enterprise approvals & multi-env</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Documentation depth</Label>
                  <Select value={state.documentation} onValueChange={(value) => setState(current => ({ ...current, documentation: value as CalculatorState["documentation"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lite">Handover notes</SelectItem>
                      <SelectItem value="detailed">Full runbooks & SOPs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-base">Customer success enablement</Label>
                  <Select value={state.successEnablement} onValueChange={(value) => setState(current => ({ ...current, successEnablement: value as CalculatorState["successEnablement"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No additional enablement</SelectItem>
                      <SelectItem value="playbook">Success playbook & lifecycle</SelectItem>
                      <SelectItem value="training">Training academy + live sessions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FeatureToggle
                  id={`${mode}-launch-support`}
                  label="Launch assets (landing page copy, GTM emails)"
                  checked={state.launchSupport}
                  onCheckedChange={(checked) => setState(current => ({ ...current, launchSupport: checked }))}
                />
                <FeatureToggle
                  id={`${mode}-launch-video`}
                  label="Launch video package"
                  checked={state.launchVideo}
                  onCheckedChange={(checked) => setState(current => ({ ...current, launchVideo: checked }))}
                />
                <div className="space-y-3">
                  <Label className="text-base">Post-launch success runway</Label>
                  <Select value={state.postLaunchSupport} onValueChange={(value) => setState(current => ({ ...current, postLaunchSupport: value as CalculatorState["postLaunchSupport"] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No support needed</SelectItem>
                      <SelectItem value="30">30 days hypercare</SelectItem>
                      <SelectItem value="60">60 days hypercare</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>
          </div>

          <aside className="flex min-w-full flex-col gap-6 lg:sticky lg:top-24 lg:min-w-[360px] lg:max-w-[380px]">
            <div className="space-y-6 rounded-2xl border border-accent/30 bg-background/90 p-6 shadow-lg">
              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">Estimated delivery</p>
                <div className="text-4xl font-heading text-accent">{quote.timelineDays} days</div>
                <div className="h-2 rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-accent transition-all duration-300"
                    style={{ width: `${Math.min(100, (quote.timelineDays / 21) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">Investment</p>
                <div className="text-5xl font-heading text-foreground">${quote.price.toLocaleString()}</div>
              </div>
              {renderEmailCapture()}
              <Button variant="outline" className="w-full" asChild>
                <a href="https://cal.com/buildwithrishi/15min" target="_blank" rel="noreferrer">
                  Book a 15-min build consult
                </a>
              </Button>
              <p className="text-xs text-muted-foreground">
                Every quote includes production deployment, Supabase setup, observability, and ownership transfer.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
              <h4 className="font-heading text-lg">Timeline breakdown</h4>
              <div className="mt-4 space-y-4">
                {quote.phases.map(phase => (
                  <div key={phase.title} className="rounded-lg border border-border/60 p-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>{phase.title}</span>
                      <span>{phase.days} day{phase.days === 1 ? "" : "s"}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      {phase.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
              <h4 className="font-heading text-lg">Driving the budget</h4>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {quote.costBreakdown.length === 0 ? (
                  <li>Baseline MVP scope with minimal extras.</li>
                ) : (
                  quote.costBreakdown.map(item => (
                    <li key={item.label} className="flex items-center justify-between gap-4 rounded-lg border border-border/50 px-3 py-2">
                      <span className="max-w-[70%]">{item.label}</span>
                      <span className={item.amount >= 0 ? "text-foreground" : "text-destructive"}>
                        {item.amount >= 0 ? "+" : "-"}${Math.abs(item.amount).toLocaleString()}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
              <h4 className="font-heading text-lg">Deliverables you’ll receive</h4>
              <ul className="mt-3 grid gap-2 text-sm text-muted-foreground">
                {quote.deliverables.map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

interface FeatureToggleProps {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

function FeatureToggle({ id, label, checked, onCheckedChange }: FeatureToggleProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-2">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}
